import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { LicenseMainFormViewData } from '../../licenseMainForm/types/LicenseMainFormViewData';
import { GzFormFieldToggleableWebComponent } from '../../../ui/gzForm/formField';
import { GzInputNumberWebComponent, GzTextareaWebComponent } from '../../../ui/gzInput';
import { GzTooltipWebComponent } from '../../../ui/gzTooltip';
import { GzHardCodedFormField } from '../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { CompanyType } from '../../../company/types/company-type';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { MinValidator } from '../../../ui/gzForm/validators/min.validator';
import { MaxValidator } from '../../../ui/gzForm/validators/max.validator';
import { BasicMonthlySubscriptionConstants } from '../BasicMonthlySubscriptionConstants';
import { GzDatePickerWebComponent } from '../../../ui/gzDatePicker';
import moment from 'moment/moment';
import { GzTextWebComponent } from '../../../ui/gzText';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { LicenseConstants } from '../../LicenseConstants';
import { CompanyData, CompanyFeatures } from '../../../company/types/company-data';
import _ from 'lodash';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';
import { MenuPosition } from '../../../types/common/types';
import { FormGroupValue } from '../../../ui/gzForm/formGroup/types/form-group-value';
import { DeepPartial } from 'typeorm';
import { LicenseMonthlyAddonsFormWebComponent } from '../../common/addonsForm';
import { AddonFormType } from '../../common/addonsForm/types/AddonFormType';
import { getSubscriptionFormEndDateMinValue } from "../../common/licenseHelper";

export class LicenseBasicMonthlySubscriptionSubFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  private defaultProductDelegateFormField = new GzHardCodedFormField<boolean>(false);
  private defaultCompanionUsableFormField = new GzHardCodedFormField<boolean>(false);
  private defaultMdrFormField = new GzHardCodedFormField<boolean>(false);

  private hasReservedSlotsValue = new GzHardCodedFormField<boolean>(false);
  public viewData: LicenseMainFormViewData | undefined;

  private agreeRemoveReservedSlotsForChildren = false;

  public dom: {
    $wrapper: HTMLElement;
    $productDetailsTitle: GzTitleWebComponent;

    $infoText: GzTextareaWebComponent;
    $subscriptionPreferencesLabel: GzTextWebComponent;

    $reserveSeatsFormField: GzFormFieldToggleableWebComponent<GzInputNumberWebComponent, number>;
    $availableSeatsText: GzTextWebComponent;

    $subscriptionEndDateFormField: GzFormFieldToggleableWebComponent<GzDatePickerWebComponent, number>;
    $subscriptionEndDateText: GzTextWebComponent;

    $autoRenewalFormField: GzFormFieldToggleableWebComponent<GzInputNumberWebComponent, number>;
    $autoRenewalText: GzTextWebComponent;

    $minimumUsageFormField: GzFormFieldToggleableWebComponent<GzInputNumberWebComponent, number>;
    $minimumUsageText: GzTextWebComponent;

    $defaultOwnUseMonthlyAddonsForm: LicenseMonthlyAddonsFormWebComponent;
    $defaultResellingMonthlyAddonsForm: LicenseMonthlyAddonsFormWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#wrapper'),
      $productDetailsTitle: this.shadow.querySelector('#product-details-title'),

      $infoText: this.shadow.querySelector('#basic-monthly-product-details-text'),
      $subscriptionPreferencesLabel: this.shadow.querySelector('#subscription-preferences-label'),

      $reserveSeatsFormField: this.shadow.querySelector('#reserve-seats-form-field'),
      $availableSeatsText: this.shadow.querySelector('#available-seats-text'),

      $subscriptionEndDateFormField: this.shadow.querySelector('#add-subscription-end-date-form-field'),
      $subscriptionEndDateText: this.shadow.querySelector('#add-subscription-end-date-text'),

      $autoRenewalFormField: this.shadow.querySelector('#set-auto-renewal-form-field'),
      $autoRenewalText: this.shadow.querySelector('#set-auto-renewal-text'),

      $minimumUsageFormField: this.shadow.querySelector('#minimum-usage-form-field'),
      $minimumUsageText: this.shadow.querySelector('#minimum-usage-text'),

      $defaultOwnUseMonthlyAddonsForm: this.shadow.querySelector('#own-use-addons-form'),
      $defaultResellingMonthlyAddonsForm: this.shadow.querySelector('#reselling-addons-form'),
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$productDetailsTitle.innerText = this.dependencies.translateFn('Product Details');

    this.dom.$infoText.innerText = this.dependencies.translateFn(
      'The company can access the following features Antimalware, Advanced Threat Control, Advanced Anti-Exploit, and Network Protection (Content Control and Network Attack Defense)'
    );
    this.dom.$subscriptionPreferencesLabel.innerText = this.dependencies.translateFn('Subscription preferences');

    this.dom.$reserveSeatsFormField.label = this.dependencies.translateFn('Reserve seats');

    this.dom.$subscriptionEndDateFormField.label = this.dependencies.translateFn('Add subscription end date');
    this.dom.$subscriptionEndDateFormField.Input.placeholder = this.dependencies.translateFn('day/month/year');
    this.dom.$subscriptionEndDateText.innerHTML = this.dependencies.translateFn(
      'After reaching the subscription end date, the company is suspended and the endpoints expire, unless auto-renewal is enabled.'
    );

    this.dom.$autoRenewalFormField.label = this.dependencies.translateFn('Set auto-renewal');
    this.dom.$autoRenewalText.innerHTML = this.dependencies.translateFn(
      'This option automatically extends the subscription validity by the number of months specified in the corresponding box.'
    );

    this.dom.$minimumUsageFormField.label = this.dependencies.translateFn('Select minimum usage');
    this.dom.$minimumUsageText.innerHTML = this.dependencies.translateFn(
      'The minimum number of endpoints that this company has committed to use on a monthly basis.'
    );
  }

  public getFormValue(): FormGroupValue {
    const value = _.clone(this.formGroup.value);

    const endDateIsOn = this.dom.$subscriptionEndDateFormField.isOn;
    const autoRenewalIsOn = this.dom.$autoRenewalFormField.isOn;
    const minimumUsageIsOn = this.dom.$minimumUsageFormField.isOn;

    const unixEndDate: number | undefined = _.get(
      value,
      ['specifics', 'license', 'endSubscription', 'endDate'],
      undefined
    );
    const features: CompanyFeatures = _.get(value, ['specifics', 'features'], {});

    if (unixEndDate) {
      // set endDate field
      const date = moment(new Date(unixEndDate * 1000)).format('YYYY-MM-DD');
      _.set(value, ['specifics', 'license', 'endSubscription', 'endDate'], date);
    } else {
      _.set(value, ['specifics', 'license', 'endSubscription', 'endDate'], undefined);
    }

    _.set(value, ['specifics', 'license', 'endSubscription', 'endDateIsSet'], endDateIsOn);
    _.set(value, ['specifics', 'license', 'endSubscription', 'autoRenewPeriodIsSet'], autoRenewalIsOn);
    _.set(value, ['specifics', 'minimalCommitment', 'usage', 'endpointsIsSet'], minimumUsageIsOn);

    // adapt to format required by CompanyData
    _.set(value, ['specifics', 'licenses'], [_.get(value, ['specifics', 'license'])]);
    _.unset(value, ['specifics', 'license']);

    // set MSP bundle flags to false
    _.set(value, ['specifics', 'features', 'bundleMspSecureUsable'], false);
    _.set(value, ['specifics', 'features', 'bundleMspSecureDelegate'], false);
    _.set(value, ['specifics', 'features', 'bundleMspSecurePlusUsable'], false);
    _.set(value, ['specifics', 'features', 'bundleMspSecurePlusDelegate'], false);
    _.set(value, ['specifics', 'features', 'bundleMspSecureExtraUsable'], false);
    _.set(value, ['specifics', 'features', 'bundleMspSecureExtraDelegate'], false);

    // set agreeRemoveReservedSlotsForChildren flag depending by the user response to warning dialog
    _.set(value, ['specifics', 'agreeRemoveReservedSlotsForChildren'], this.agreeRemoveReservedSlotsForChildren);

    _.set(
      value,
      ['specifics', 'features'],
      _.merge(
        features,
        this.dom.$defaultOwnUseMonthlyAddonsForm.value,
        this.dom.$defaultResellingMonthlyAddonsForm.value
      )
    );

    return value;
  }

  public setViewData(viewData: LicenseMainFormViewData) {
    // view data already set, skip
    if (this.viewData) {
      this.updateViewData(viewData);
      return;
    }

    this.createFormGroup();
    this.updateViewData(viewData);
  }

  public setCompanyData(company: CompanyData) {
    const clonedCompany = _.cloneDeep(company);

    const monthlyLicense = CompanyData.getMonthlyLicense(clonedCompany);
    if (monthlyLicense?.endSubscription?.endDate) {
      const timezone = this.dependencies.getTimezone();
      monthlyLicense.endSubscription.endDate = moment(monthlyLicense.endSubscription.endDate)
        .tz(timezone)
        .format('YYYY-MM-DD');
    }

    this.setSubscriptionDetails(clonedCompany);

    this.setUpReserveSeatsFormField();
  }

  public setSubscriptionDetails(company: DeepPartial<CompanyData>) {
    const license = CompanyData.getFirstBaseLicense(company);

    if (!license) {
      throw new Error('setSubscriptionDetails: company does not have license');
    }

    // extract only relevant fields from company
    const subscriptionDetails = {
      specifics: {
        hasReservedSlots: !!company.specifics?.hasReservedSlots,
        license: {
          endSubscription: {
            endDate: license.endSubscription?.endDate,
            endDateIsSet: !!license.endSubscription?.endDateIsSet,
            autoRenewPeriod: license.endSubscription?.autoRenewPeriod,
            autoRenewPeriodIsSet: !!license.endSubscription?.autoRenewPeriodIsSet,
          },
          total: license.total,
        },
        minimalCommitment: {
          usage: {
            endpoints: company.specifics?.minimalCommitment?.usage?.endpoints,
            endpointsIsSet: !!company.specifics?.minimalCommitment?.usage?.endpointsIsSet,
          },
        },
      },
    };

    // load subscription details into form
    this.formGroup.value = subscriptionDetails;

    if (!subscriptionDetails.specifics?.hasReservedSlots) {
      // if there are no reserved seats, the license.total field is not set so the 'value' setter does not overwrite the form value
      this.dom.$reserveSeatsFormField.setValueWithoutToggleUpdate(
        LicenseConstants.MonthlySubscription.ReserveSeats.Default
      );
      this.dom.$reserveSeatsFormField.setToggleFormValue(false);
    }

    const endSubscription = subscriptionDetails.specifics.license.endSubscription;
    const hasEndSubscriptionDateSet = endSubscription?.endDateIsSet ?? false;

    if (!endSubscription?.endDateIsSet) {
      // End subscription may be inherited. In this case, we have a valid endDate (the inherited one) but endDateIsSet is false.
      this.dom.$subscriptionEndDateFormField.value = undefined;
      this.dom.$subscriptionEndDateFormField.setToggleFormValue(false);
    } else {
      const endDate = endSubscription.endDate;
      this.dom.$subscriptionEndDateFormField.value = endDate ? moment(endDate).unix() : 0;
      this.dom.$subscriptionEndDateFormField.validate();
    }

    if (!endSubscription?.autoRenewPeriodIsSet) {
      // Auto renewal period may be inherited. In this case, we have a valid period (the inherited one) but autoRenewPeriodIsSet is false.
      this.dom.$autoRenewalFormField.value = LicenseConstants.MonthlySubscription.AutoRenewal.Default;
      this.dom.$autoRenewalFormField.setToggleFormValue(false);
    }

    this.dom.$autoRenewalFormField.disabled = !hasEndSubscriptionDateSet;
    this.dom.$autoRenewalFormField.touched = true;

    if (!subscriptionDetails.specifics.minimalCommitment.usage.endpointsIsSet) {
      // if minimum usage is not set, the specifics.minimalCommitment field is set to undefined on the company record
      // which comes from the backend
      this.dom.$minimumUsageFormField.value = LicenseConstants.MonthlySubscription.MinimumUsage.Default;
      this.dom.$minimumUsageFormField.setToggleFormValue(false);
    }
  }

  buildSuccessView(data: LicenseMainFormViewData) {
    this.setDefaultValues();
    this.buildDefaultMonthlyAddonsForm();
  }

  buildErrorView(error: Error) {}

  public updateMinCommitmentValidators() {
    this.setUpMinimumUsageFormField();
  }

  public updateHasReservedSlotsValue() {
    this.hasReservedSlotsValue.setValue(this.dom.$reserveSeatsFormField.isOn);
  }

  public updateAvailableSeatsText() {
    if (!this.dom.$reserveSeatsFormField.isOn) {
      toggleVisibility(this.dom.$availableSeatsText, false);
      return;
    }

    const maxSeats = this.getMaxSlotsToReserve();
    const reservedSeats = this.dom.$reserveSeatsFormField.value as number;

    const availableSeats = Math.max(0, maxSeats - reservedSeats);

    toggleVisibility(this.dom.$availableSeatsText, reservedSeats !== 0);

    this.dom.$availableSeatsText.textContent = this.dependencies.translateFn('({availableSeats} still available)', {
      availableSeats,
    });
  }

  public updateDefaultProductDelegateFeature(companyType: CompanyType) {
    switch (companyType) {
      case CompanyType.Customer:
        this.defaultProductDelegateFormField.setValue(false);
        break;
      case CompanyType.Partner:
        this.defaultProductDelegateFormField.setValue(true);
        break;
    }
  }

  public updateAutoRenewalFormFieldAvailability() {
    this.dom.$autoRenewalFormField.disabled = !this.dom.$subscriptionEndDateFormField.isOn;
    this.dom.$autoRenewalFormField.setToggleFormValue(this.dom.$subscriptionEndDateFormField.isOn);
  }

  private updateViewData(viewData: LicenseMainFormViewData) {
    this.viewData = viewData;

    this.setupSubscriptionEndDateField();

    this.setUpReserveSeatsFormField();
    this.setUpSubscriptionEndDateFormField();
    this.setUpAutoRenewalFormField();
    this.setEnabledMinimumUsageFormField();
    this.setUpMinimumUsageFormField();
  }

  private buildDefaultMonthlyAddonsForm() {
    this.dom.$defaultOwnUseMonthlyAddonsForm.injectDependencies(this.dependencies);
    this.dom.$defaultOwnUseMonthlyAddonsForm
      .start()
      .then(() => {
        this.dom.$defaultOwnUseMonthlyAddonsForm.formType = AddonFormType.OwnUse;
      })
      .catch(console.error);

    this.dom.$defaultResellingMonthlyAddonsForm.injectDependencies(this.dependencies);
    this.dom.$defaultResellingMonthlyAddonsForm
      .start()
      .then(() => {
        this.dom.$defaultResellingMonthlyAddonsForm.formType = AddonFormType.Resell;
      })
      .catch(console.error);
  }

  private setDefaultValues() {
    this.dom.$reserveSeatsFormField.value = BasicMonthlySubscriptionConstants.DefaultValueForReserveSeatsField;
    this.dom.$reserveSeatsFormField.setToggleFormValue(false);

    this.dom.$minimumUsageFormField.value = BasicMonthlySubscriptionConstants.DefaultValueForMinimumUsageField;
    this.dom.$minimumUsageFormField.setToggleFormValue(false);
    this.dom.$minimumUsageFormField.disabledValue = BasicMonthlySubscriptionConstants.DefaultValueForMinimumUsageField;

    this.dom.$subscriptionEndDateFormField.disabledValue =
      BasicMonthlySubscriptionConstants.DisabledValueForEndDateFormField;

    this.dom.$autoRenewalFormField.setValueWithoutToggleUpdate(
      BasicMonthlySubscriptionConstants.DefaultValueForAutoRenewalField
    );
    this.dom.$autoRenewalFormField.disabled = true;
  }

  private getMinSlotsToReserve() {
    if (!this.viewData?.companyLicenseInformation.reservationSupported) {
      return BasicMonthlySubscriptionConstants.MinValueForReserveSeatsValidator;
    }

    const minSlotsToReserve = this.viewData.companyLicenseInformation.minSlotsToReserve;
    if (typeof minSlotsToReserve !== 'number' || minSlotsToReserve <= 0) {
      return BasicMonthlySubscriptionConstants.MinValueForReserveSeatsValidator;
    }

    return minSlotsToReserve;
  }

  private getMaxSlotsToReserve() {
    if (!this.viewData?.companyLicenseInformation.reservationSupported) {
      return 0;
    }

    const maxSlotsToReserve = this.viewData.companyLicenseInformation.maxSlotsToReserve;
    if (typeof maxSlotsToReserve !== 'number') {
      return 0;
    }

    return maxSlotsToReserve;
  }

  private setupSubscriptionEndDateField() {
    this.dom.$subscriptionEndDateFormField.Input.menuPosition =
      this.viewData?.context === CompanyActionContext.Edit ? MenuPosition.RIGHT : MenuPosition.LEFT;

    this.dom.$subscriptionEndDateFormField.Input.pickerStrings = {
      currentDay: this.dependencies.translateFn('Today'),
    };

    const timezone = this.dependencies.getTimezone();

    this.dom.$subscriptionEndDateFormField.Input.timezone = timezone;
    this.dom.$subscriptionEndDateFormField.Input.min = getSubscriptionFormEndDateMinValue(timezone);
    this.dom.$subscriptionEndDateFormField.Input.max = 0;

    if (!this.viewData?.companyLicenseInformation.parentEndSubscriptionEndDateLimit) {
      return;
    }

    const maxSubscriptionEndDate = moment(this.viewData.companyLicenseInformation.parentEndSubscriptionEndDateLimit)
      .tz(timezone)
      .format();
    this.dom.$subscriptionEndDateFormField.Input.max = moment(maxSubscriptionEndDate).format('X');
  }

  private createFormGroup() {
    this.formGroup = FormGroup.build({
      specifics: {
        minimalCommitment: {
          usage: {
            endpoints: this.dom.$minimumUsageFormField,
          },
        },

        hasReservedSlots: this.hasReservedSlotsValue,
        license: {
          endSubscription: {
            autoRenewPeriod: this.dom.$autoRenewalFormField,
            endDate: this.dom.$subscriptionEndDateFormField,
          },
          total: this.dom.$reserveSeatsFormField,
        },

        inheritedLicense: new GzHardCodedFormField<boolean>(true),

        features: {
          defaultProductDelegate: this.defaultProductDelegateFormField,
          companionProductDelegate: new GzHardCodedFormField<boolean>(false),
          companionUsable: this.defaultCompanionUsableFormField,

          mdrAdviceUsable: this.defaultMdrFormField,
          mdrAdviceDelegate: this.defaultMdrFormField,
        },
      },
    });
  }

  private setUpReserveSeatsFormField(): void {
    const minSlotsToReserve = this.getMinSlotsToReserve();
    const maxSlotsToReserve = this.getMaxSlotsToReserve();

    if (!maxSlotsToReserve) {
      this.dom.$reserveSeatsFormField.setEnabled(false);
      return;
    }

    this.dom.$reserveSeatsFormField.setEnabled(true);

    this.dom.$reserveSeatsFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MinValidator(
        minSlotsToReserve,
        this.dependencies.translateFn('The minimum value for this field is {minSlotsToReserve}', {
          minSlotsToReserve,
        })
      ),
      MaxValidator(
        maxSlotsToReserve,
        this.dependencies.translateFn('The maximum value for this field is {maxSlotsToReserve}', {
          maxSlotsToReserve,
        })
      ),
    ];

    this.dom.$reserveSeatsFormField.Input.min = BasicMonthlySubscriptionConstants.MinValueForReserveSeatsFieldInput;
  }

  private setUpSubscriptionEndDateFormField(): void {
    this.dom.$subscriptionEndDateFormField.Validators = [
      // RequiredValidator would not work because it will give 'valid' result for value 0
      MinValidator(1, this.commonStrings.requiredField),
    ];
  }

  private setUpAutoRenewalFormField(): void {
    this.dom.$autoRenewalFormField.disabledValue = BasicMonthlySubscriptionConstants.DisabledValueForAutoRenewalField;

    this.dom.$autoRenewalFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MinValidator(
        BasicMonthlySubscriptionConstants.MinValueForAutoRenewalValidator,
        this.dependencies.translateFn('The minimum value for this field is {minValue}', {
          minValue: BasicMonthlySubscriptionConstants.MinValueForAutoRenewalValidator,
        })
      ),
      MaxValidator(
        BasicMonthlySubscriptionConstants.MaxValueForAutoRenewalValidator,
        this.dependencies.translateFn('The maximum value for this field is {maxAutoRenewal}', {
          maxAutoRenewal: BasicMonthlySubscriptionConstants.MaxValueForAutoRenewalValidator,
        })
      ),
    ];
    this.dom.$autoRenewalFormField.Input.min = BasicMonthlySubscriptionConstants.MinValueForAutoRenewalFieldInput;
  }

  private setEnabledMinimumUsageFormField(): void {
    const shouldDisableMinUsage = this.viewData?.companyLicenseInformation.maxSlotsToReserve === 0;
    this.dom.$minimumUsageFormField.setEnabled(!shouldDisableMinUsage);
  }

  private setUpMinimumUsageFormField(): void {
    const maxUsage = this.dom.$reserveSeatsFormField.isOn
      ? this.dom.$reserveSeatsFormField.value
      : this.getMaxSlotsToReserve();

    const validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MinValidator(
        LicenseConstants.MonthlySubscription.MinimumUsage.MinValidator,
        this.dependencies.translateFn('The minimum value for this field is {minValue}', {
          minValue: LicenseConstants.MonthlySubscription.MinimumUsage.MinValidator,
        })
      ),
    ];

    if (maxUsage) {
      validators.push(
        MaxValidator(
          maxUsage,
          this.dependencies.translateFn(
            'Number of license seats exceeded. Enter a value lower than or equal to: {maxUsage}',
            {
              maxUsage,
            }
          )
        )
      );
    }

    this.dom.$minimumUsageFormField.Validators = validators;

    this.dom.$minimumUsageFormField.Input.min = LicenseConstants.MonthlySubscription.MinimumUsage.MinInput;
  }

  public updateReservedSeatsFormFieldAvailability(reservedSeatsToggleIsOn: boolean) {
    this.dom.$reserveSeatsFormField.disabled = !reservedSeatsToggleIsOn;
    this.dom.$reserveSeatsFormField.setToggleFormValue(reservedSeatsToggleIsOn);
  }

  public setAgreeRemoveReservedSlotsForChildren(agreeRemoveReservedSlotsForChildren: boolean) {
    this.agreeRemoveReservedSlotsForChildren = agreeRemoveReservedSlotsForChildren;
  }
}
